import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { view, contentEditable, textFormatting, i18next, dialogs, bowser } from '@yola/ws-sdk';
import { constants as subscriptionManagerConstants } from '@yola/subscription-manager-js';
import upsell from 'src/js/modules/upsell';
import Trigger from 'src/js/modules/common/components/trigger';
import withSelection from 'src/js/modules/utils/with-selection';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import useTextDecorations from '../hooks/use-text-decorations';
import wrapInDecorationElement from '../helpers/wrap-in-decoration-element';
import restoreRange from '../helpers/restore-range';
import setSelectionByNodePath from '../helpers/set-selection-by-node-path';
import getInitialDecorationAttributes from '../helpers/get-initial-decoration-attributes';

const { Icon } = designSystem;
const {
  wrappersTypes: { decoration },
} = textFormatting.constants;
const {
  hostingPackageFeatureNames: { PREMIUM_TEXT_DECORATIONS },
} = subscriptionManagerConstants;

const TRIGGER_ID = 'text-decoration';

const TextDecorationTrigger = ({ elementId }) => {
  const element = view.accessors.getLiveElement(elementId);
  const { decorations } = useTextDecorations();
  const focusedElementInfo = useRef(contentEditable.helpers.getFocusedElementInfo());

  const restoreElement = () => {
    element.replaceChildren(...focusedElementInfo.current.clonedRootElement.childNodes);
    const selection = setSelectionByNodePath(element, focusedElementInfo.current.selectionData);
    restoreRange(selection, elementId);
  };

  const showTextDecorationSettingsDialog = (elements) => {
    if (bowser.mobile || bowser.tablet) {
      element.blur(); // Hide the keyboard
    }

    dialogs.operations.show(dialogTypes.TEXT_DECORATION_SETTINGS_DIALOG, {
      elements,
      wrapperElement: element,
      premiumAvailable: upsell.verifiers.isFeatureAvailable(PREMIUM_TEXT_DECORATIONS),
      onSubmit: (attributes) => {
        restoreElement();
        wrapInDecorationElement(elementId, attributes);
      },
      onCancel: () => {
        restoreElement();
      },
    });
  };

  const handleClick = () => {
    focusedElementInfo.current = contentEditable.helpers.getFocusedElementInfo();
    restoreRange(textFormatting.accessors.getAdvancedSelection(), elementId);

    const wrappedNodes = wrapInDecorationElement(
      elementId,
      getInitialDecorationAttributes(element, decorations[0].id)
    );
    const selection = textFormatting.accessors.getAdvancedSelection();
    const range = selection.getRangeAt(0);
    const affectedNodes = [...wrappedNodes].filter((node) => range.containsNodeText(node));

    if (affectedNodes.length) {
      showTextDecorationSettingsDialog(affectedNodes);
    }
  };

  return (
    <Trigger data-tip={i18next.t('Text effects')} id={TRIGGER_ID} onClick={handleClick}>
      <Icon glyph="text-effects" size="24" strokeWidth="1.3" />
    </Trigger>
  );
};

TextDecorationTrigger.propTypes = {
  elementId: PropTypes.string.isRequired,
};

export default withSelection(TextDecorationTrigger);
